import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Select, Input, Button, Upload, message, Steps, Row, Col, Space, ConfigProvider, Typography, App, Tooltip } from 'antd';
import { ChartBarIcon } from '@heroicons/react/24/outline';
import { motion, AnimatePresence } from 'framer-motion';
import SectionTitle from './text_components/SectionTitle';
import { EditOutlined, InboxOutlined, InfoCircleOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import { useMediaQuery } from 'react-responsive';
import axios from 'axios';
import { getCurrentUserId } from '../utils/userUtils';
import dayjs from 'dayjs';
import { useCompany } from '../contexts/CompanyContext';

const { Option } = Select;
const { Step } = Steps;
const { Title } = Typography;
const { Dragger } = Upload;

const StyledCard = styled(motion.div)`
  background: white;
  border-radius: 20px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  padding: 2rem;
  transition: all 0.3s ease;
  border: 1px solid #bfdbfe;

  &:hover {
    box-shadow: 0 15px 40px rgba(0, 0, 0, 0.15);
    transform: translateY(-5px);
  }
`;

const StyledSelect = styled(Select)`
  .ant-select-selector {
    border-radius: 10px !important;
    border: 2px solid #e6e6e6 !important;
    transition: all 0.3s ease;
  }

  &:hover .ant-select-selector,
  &.ant-select-focused .ant-select-selector {
    border-color: #4a90e2 !important;
    box-shadow: 0 0 0 2px rgba(74, 144, 226, 0.2) !important;
  }
`;

const StyledButton = styled(Button)`
  border-radius: 10px;
  height: 40px;
  font-weight: 600;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  }
`;

const StyledSteps = styled(Steps)`
  .ant-steps-item-icon {
    background: white;
    border-color: white;
    color: white;
  }

  .ant-steps-item-title {
    font-weight: 600;
  }

  .ant-steps-item-finish .ant-steps-item-icon {
    background: #52c41a;
    border-color: #52c41a;
  }

  .ant-steps-item-finish .ant-steps-icon {
    color: white !important;
  }

  .ant-steps-item-finish .ant-steps-item-tail::after {
    background-color: #52c41a;
  }

  .ant-steps-item-process .ant-steps-item-icon {
    white !important;
    border-color: #4a90e2;
  }

  .ant-steps-item-process .ant-steps-icon {
    color: white !important;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
`;

const LoadingCircle = styled(motion.div)`
  width: 15px;
  height: 15px;
  margin: 0 5px;
  border-radius: 50%;
  background: #60a5fa;
  display: inline-block;
`;

const renderLabel = (label, tooltip) => (
  <div className="flex items-center" style={{ marginBottom: '0.5rem' }}>
    <span>{label}</span>
    <Tooltip title={tooltip}>
      <InfoCircleOutlined className="text-gray-400 hover:text-gray-600 ml-2" />
    </Tooltip>
  </div>
);

const Reporting = () => {
  const [messageApi, contextHolder] = message.useMessage();
  const { company, isCustomerView } = useCompany();

  const [reportSettings, setReportSettings] = useState({
    reportType: 'quarterly',
    years: [new Date().getFullYear().toString()],
    quarters: [],
    compareYears: 'Yes',
    clients: [],
    marketOverview: 'Use Default Market Overview',
    marketOverviewText: ''
  });

  const [allQuarters] = useState(['Q1', 'Q2', 'Q3', 'Q4']);
  const [allClients, setAllClients] = useState([]);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  const userId = getCurrentUserId();
  // console.log("USER ID on line ~151", userId);

  // IMPORTANT: Adjusted the base URL to match the endpoint without "/api"

  const getPreviousQuarter = () => {
    const today = new Date();
    const currentMonth = today.getMonth();
    const currentQuarter = Math.floor(currentMonth / 3) + 1;
    let previousQuarter = currentQuarter - 1;
    if (previousQuarter === 0) {
      previousQuarter = 4;
    }
    return `Q${previousQuarter}`;
  };


  const fetchClients = useCallback(async (year) => {
    // If year is undefined, use the current year
    const effectiveYear = year || new Date().getFullYear().toString();
    console.log("DEBUGGING YEAR: ", effectiveYear);
    try {
      console.log("USING EFFECTIVE YEAR: ", effectiveYear);
      const response = await axios.get(`https://plansync-backend-234288438538.us-central1.run.app/api/clients/${userId}/${effectiveYear}`);
      
      // Filter clients for customer users
      if (isCustomerView && company) {
        const filteredClients = response.data.clients.filter(client => client === company);
        setAllClients(filteredClients);
        // Also pre-select the company for customer users
        setReportSettings(prev => ({
          ...prev,
          clients: [company]
        }));
      } else {
        setAllClients(response.data.clients);
      }
    } catch (error) {
      console.error('Error fetching clients:', error);
      messageApi.error('Failed to fetch clients. Please try again.');
    }
  }, [userId, messageApi, isCustomerView, company]);

  useEffect(() => {
    const fetchUserData = async () => {
      setIsLoading(true);
      try {
        const response = await axios.get(`https://plansync-backend-234288438538.us-central1.run.app/api/user-settings/${userId}`);
        const data = response.data;
        // Check if the current year is not in the data.years and if it is not add it as a string
        const currentYear = dayjs().year().toString();
        if (!data.years.includes(currentYear)) {
          data.years.push(currentYear);
        }
        console.log("DEBUGGING DATA OF THE YEARS: ", data.years);
        setIsLoading(false);
        return data;
      } catch (error) {
        console.error('Error fetching user data:', error);
        messageApi.error('Failed to fetch user data. Please try again.');
        setIsLoading(false);
        return null;
      }
    };

    (async () => {
      const data = await fetchUserData();
      if (data) {
        setUserData(data);
        setReportSettings(prev => ({
          ...prev,
          quarters: [getPreviousQuarter()]
        }));
      }
    })();
  }, [userId, messageApi, fetchClients]);

  useEffect(() => {
    if (reportSettings.years.length > 0) {
      fetchClients(reportSettings.years[-1]);
    }
  }, [reportSettings.years, fetchClients]);

  const availableYears = useMemo(() => {
    return userData ? userData.years.filter(year => !reportSettings.years.includes(year)) : [];
  }, [reportSettings.years, userData]);

  const availableQuarters = useMemo(() => {
    return allQuarters.filter(quarter => !reportSettings.quarters.includes(quarter));
  }, [reportSettings.quarters, allQuarters]);

  const handleChange = (key, value) => {
    setReportSettings(prev => ({ ...prev, [key]: value }));
  };

  const handleGenerateReport = async () => {
    setLoading(true);
    const endpoint = reportSettings.reportType === 'annual' 
      ? `https://plansync-backend-234288438538.us-central1.run.app/api/generate-annual-report/` 
      : `https://plansync-backend-234288438538.us-central1.run.app/api/generate-quarterly-report`;

    try {
      const payload = preparePayload();
      // console.log("Payload being sent:", payload);
      // console.log("ENDPOINT: ", endpoint);

      const response = await axios.post(endpoint, payload, {
        responseType: 'blob',
        headers: { 'Content-Type': 'application/json' },
      });

      // console.log("Response received:", response);

      const contentType = response.headers['content-type'];

      // console.log("CONTENT TYPE: ", contentType)
      if (contentType && contentType.includes('application/json')) {
        const reader = new FileReader();
        reader.onload = () => {
          const errorMessage = JSON.parse(reader.result);
          console.error('Error from server:', errorMessage);
          messageApi.error(`Failed to generate report: ${errorMessage.detail || 'Unknown error'}`);
        };
        reader.readAsText(response.data);
      } else if (response.data.size > 0) {
        const blob = new Blob([response.data], { type: contentType });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${reportSettings.reportType}_401k_Reports.zip`);
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
        messageApi.success('Report generated and downloaded successfully');
      } else {
        throw new Error('Received empty file');
      }
    } catch (error) {
      console.error('Error generating report:', error);
      if (error.response) {
        console.error('Error response:', error.response.data);
        const reader = new FileReader();
        reader.onload = () => {
          const errorMessage = JSON.parse(reader.result);
          messageApi.error(`Failed to generate report: ${errorMessage.detail || 'Unknown error'}`);
        };
        reader.readAsText(error.response.data);
      } else {
        messageApi.error('Failed to generate report. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const preparePayload = () => {
    console.log("PAYLOAD BEING PREPARED: ", reportSettings);
    console.log("USER DATA: ", userData);
    const payload = {
      selected_years: reportSettings.years.map(Number),
      selected_clients: reportSettings.clients,
      header_image_file_path: userData ? userData.header_image : '',
      footer_image_file_path: userData ? userData.footer_image : '',
      color_code: userData ? userData.color_scheme : '#494ce1',
      user_id: userId,
      advisors: [],
      advisors_professional_designations: [],
      // This must always be provided:
    };
    console.log("PAYLOAD: ", payload);

    // Collect advisors if present in userData
    const advisors = [];
    const advisorsDesignations = [];
    if (userData && userData.advisor1 && userData.advisor1.Name) {
      advisors.push(userData.advisor1.Name);
      advisorsDesignations.push((userData.advisor1.Designations || []).join(", "));
    }
    if (userData && userData.advisor2 && userData.advisor2.Name) {
      advisors.push(userData.advisor2.Name);
      advisorsDesignations.push((userData.advisor2.Designations || []).join(", "));
    }
    if (userData && userData.advisor3 && userData.advisor3.Name) {
      advisors.push(userData.advisor3.Name);
      advisorsDesignations.push((userData.advisor3.Designations || []).join(", "));
    }

    payload.advisors = advisors;
    payload.advisors_professional_designations = advisorsDesignations;

    if (reportSettings.reportType === 'annual') {
      return {
        ...payload,
        compare_years: reportSettings.compareYears === 'Yes',
        advisor_company_name: userData ? userData.company_name : '',
      };
    } else {
      return {
        ...payload,
        selected_quarters: reportSettings.quarters.map(q => parseInt(q.slice(1))),
        market_overview_selections: [reportSettings.marketOverview],
        market_overview_text: reportSettings.marketOverview === 'Type My Own Market Overview' 
          ? reportSettings.marketOverviewText 
          : '',
      };
    }
  };

  const handleFileUpload = (info) => {
    const { status } = info.file;
    if (status === 'done') {
      // setMarketOverviewFile(info.file.originFileObj);
      messageApi.success(`${info.file.name} file uploaded successfully.`);
    } else if (status === 'error') {
      messageApi.error(`${info.file.name} file upload failed.`);
    }
  };

  const beforeUpload = (file) => {
    const isWordOrTxt = file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' || file.type === 'text/plain';
    if (!isWordOrTxt) {
      messageApi.error('You can only upload Word (.docx) or Text (.txt) files!');
    }
    return isWordOrTxt || Upload.LIST_IGNORE;
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.3,
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: 'spring',
        stiffness: 100,
        damping: 10
      }
    }
  };

  const handleEdit = (section) => {
    switch(section) {
      case 'reportType':
        setCurrentStep(0);
        break;
      case 'reportParameters':
      case 'additionalInfo':
        setCurrentStep(1);
        break;
      default:
        break;
    }
  };

  const isStepValid = (step) => {
    switch(step) {
      case 0:
        return true;
      case 1:
        return reportSettings.years.length > 0 && 
               reportSettings.clients.length > 0 &&
               (reportSettings.reportType === 'quarterly' ? reportSettings.quarters.length > 0 : true) &&
               (reportSettings.reportType === 'annual' ? reportSettings.compareYears !== '' : true) &&
               reportSettings.marketOverview !== '';
      default:
        return true;
    }
  };

  const handleStepChange = (current) => {
    for (let i = 0; i < current; i++) {
      if (!isStepValid(i)) {
        messageApi.error('Please complete the previous steps before proceeding.');
        return;
      }
    }
    setCurrentStep(current);
  };

  const pageVariants = {
    initial: { opacity: 0, y: 10 },
    in: { opacity: 1, y: 0 },
    out: { opacity: 0, y: -10 }
  };

  const pageTransition = {
    type: 'tween',
    ease: 'easeInOut',
    duration: 0.3
  };

  const steps = [
    {
      title: 'Report Type',
      content: (
        <div>
          {renderLabel(
            "Report Type",
            "Annual reports provide yearly summaries, while quarterly reports offer more frequent updates"
          )}
          <StyledSelect
            style={{ width: '100%' }}
            value={reportSettings.reportType}
            onChange={(value) => handleChange('reportType', value)}
            className="text-lg"
          >
            <Option value="annual">Annual Report</Option>
            <Option value="quarterly">Quarterly Report</Option>
          </StyledSelect>
        </div>
      ),
    },
    {
      title: 'Report Details',
      content: (
        <>
          <div>
            {renderLabel(
              "Reporting Years",
              "Select one or more years for your report. For annual reports, multiple years will generate separate reports"
            )}
            <StyledSelect
              mode="multiple"
              style={{ width: '100%' }}
              placeholder="Select years"
              value={reportSettings.years}
              onChange={(value) => handleChange('years', value)}
            >
              {availableYears.map(year => (
                <Option key={year} value={year}>{year}</Option>
              ))}
            </StyledSelect>
          </div>
          {reportSettings.reportType === 'quarterly' && (
            <div className="mt-4">
              {renderLabel(
                "Quarters",
                "Select the quarters you want to include in your report. Multiple quarters will generate separate reports"
              )}
              <StyledSelect
                mode="multiple"
                style={{ width: '100%' }}
                placeholder="Select quarters"
                value={reportSettings.quarters}
                onChange={(value) => handleChange('quarters', value)}
              >
                {availableQuarters.map(quarter => (
                  <Option key={quarter} value={quarter}>{quarter}</Option>
                ))}
              </StyledSelect>
            </div>
          )}
          {reportSettings.reportType === 'annual' && (
            <div className="mt-4">
              {renderLabel(
                "Compare to Previous Year",
                "Choose 'Yes' to include year-over-year comparisons in your annual report"
              )}
              <StyledSelect
                style={{ width: '100%' }}
                value={reportSettings.compareYears}
                onChange={(value) => handleChange('compareYears', value)}
              >
                <Option value="Yes">Yes</Option>
                <Option value="No">No</Option>
              </StyledSelect>
            </div>
          )}
          <div className="mt-4">
            {renderLabel(
              "Clients",
              "Select individual clients or choose 'All Clients' to generate reports for your entire client base"
            )}
            <StyledSelect
              mode="multiple"
              style={{ width: '100%' }}
              placeholder="Select clients"
              value={reportSettings.clients}
              disabled={isCustomerView}
              onChange={(value) => {
                if (value.includes('All Clients')) {
                  handleChange('clients', allClients);
                } else if (reportSettings.clients.includes('All Clients')) {
                  handleChange('clients', []);
                } else {
                  handleChange('clients', value);
                }
              }}
            >
              {!isCustomerView && <Option key="All Clients" value="All Clients">All Clients</Option>}
              {allClients.map(client => (
                <Option key={client} value={client}>{client}</Option>
              ))}
            </StyledSelect>
          </div>
          {reportSettings.reportType === 'quarterly' && (
            <>
              <div className="mt-4">
                {renderLabel(
                  "Market Overview",
                  "Choose between our pre-written market overview, uploading your own, typing your own, or excluding it entirely"
                )}
                <StyledSelect
                  style={{ width: '100%'}}
                  value={reportSettings.marketOverview}
                  onChange={(value) => handleChange('marketOverview', value)}
                >
                  <Option value="Use Default Market Overview">Use Default Market Overview</Option>
                  {/* <Option value="Upload My Own Market Overview">Upload My Own Market Overview</Option> */}
                  <Option value="Type My Own Market Overview">Type My Own Market Overview</Option>
                  <Option value="Do Not Include a Market Overview">Do Not Include a Market Overview</Option>
                </StyledSelect>
              </div>
              {reportSettings.marketOverview === 'Upload My Own Market Overview' && (
                <div className="mt-4">
                  {renderLabel(
                    "Upload Market Overview",
                    "Upload a Word (.docx) or text (.txt) file containing your custom market overview"
                  )}
                  <Dragger
                    name="file"
                    multiple={false}
                    action="/upload"
                    onChange={handleFileUpload}
                    accept=".docx,.txt"
                    beforeUpload={beforeUpload}
                  >
                    <p className="ant-upload-drag-icon">
                      <InboxOutlined />
                    </p>
                    <p className="ant-upload-text">Click or drag file to this area to upload</p>
                    <p className="ant-upload-hint">
                      Support for a single .docx or .txt file upload.
                    </p>
                  </Dragger>
                </div>
              )}
              {reportSettings.marketOverview === 'Type My Own Market Overview' && (
                <div className="mt-4">
                  {renderLabel(
                    "Type Market Overview",
                    "Enter your custom market overview text directly"
                  )}
                  <Input.TextArea
                    placeholder="Type your market overview here..."
                    value={reportSettings.marketOverviewText}
                    onChange={(e) => handleChange('marketOverviewText', e.target.value)}
                    rows={6}
                    className="w-full p-2 border border-gray-300 rounded-md"
                  />
                </div>
              )}
            </>
          )}
        </>
      ),
    },
    {
      title: 'Review',
      content: (
        <div>
          <h3 className="text-lg font-semibold mb-4 text-gray-700">Review Your Selections</h3>
          <Space direction="vertical" size="middle" style={{ display: 'flex' }}>
            <div className="flex justify-between items-center" style={{ marginBottom: '0.5rem' }}>
              <span>Report Type: {reportSettings.reportType.charAt(0).toUpperCase() + reportSettings.reportType.slice(1)}</span>
              <StyledButton icon={<EditOutlined />} onClick={() => handleEdit('reportType')}>Edit</StyledButton>
            </div>
            <div className="flex justify-between items-center">
              <span>Years: {reportSettings.years.join(', ')}</span>
              <StyledButton icon={<EditOutlined />} onClick={() => handleEdit('reportParameters')}>Edit</StyledButton>
            </div>
            {reportSettings.reportType === 'quarterly' && (
              <div className="flex justify-between items-center">
                <span>Quarters: {reportSettings.quarters.join(', ')}</span>
                <StyledButton icon={<EditOutlined />} onClick={() => handleEdit('reportParameters')}>Edit</StyledButton>
              </div>
            )}
            {reportSettings.reportType === 'annual' && (
              <div className="flex justify-between items-center">
                <span>Compare to Previous Year: {reportSettings.compareYears}</span>
                <StyledButton icon={<EditOutlined />} onClick={() => handleEdit('reportParameters')}>Edit</StyledButton>
              </div>
            )}
            <div className="flex justify-between items-center">
              <span>Clients: {reportSettings.clients.join(', ')}</span>
              <StyledButton icon={<EditOutlined />} onClick={() => handleEdit('reportParameters')}>Edit</StyledButton>
            </div>
            {reportSettings.reportType === 'quarterly' && (
              <div className="flex justify-between items-center">
                <span>
                  Market Overview: {reportSettings.marketOverview}
                  {reportSettings.marketOverview === 'Type My Own Market Overview' && (
                    <div className="text-sm text-gray-500 mt-1">
                      {reportSettings.marketOverviewText.substring(0, 50)}
                      {reportSettings.marketOverviewText.length > 50 ? '...' : ''}
                    </div>
                  )}
                </span>
                <StyledButton icon={<EditOutlined />} onClick={() => handleEdit('reportParameters')}>Edit</StyledButton>
              </div>
            )}
          </Space>
        </div>
      ),
    },
  ];

  const next = () => {
    setCurrentStep(currentStep + 1);
  };

  const prev = () => {
    setCurrentStep(currentStep - 1);
  };

  const isMobile = useMediaQuery({ maxWidth: 767 });

  if (isLoading) {
    return (
      <LoadingContainer>
        <div>
          {[0, 1, 2].map((index) => (
            <LoadingCircle
              key={index}
              animate={{
                scale: [1, 1.2, 1],
                opacity: [0.6, 1, 0.6],
                y: [0, -20, 0],
              }}
              transition={{
                duration: 1,
                repeat: Infinity,
                delay: index * 0.2,
                ease: "easeInOut"
              }}
            />
          ))}
        </div>
      </LoadingContainer>
    );
  }

  return (
    <App>
      <ConfigProvider
        theme={{
          token: {
            colorPrimary: '#4a90e2',
            borderRadius: 10,
            fontFamily: "'Inter', sans-serif",
          },
        }}
      >
        {contextHolder}
        <Row justify="center" align="middle" className={`min-h-screen ${isMobile ? 'p-0' : 'py-8'}`}>
          <Col xs={24} sm={24} md={22} lg={20} xl={18} className={isMobile ? 'h-screen' : ''}>
            <StyledCard
              initial="hidden"
              animate="visible"
              variants={containerVariants}
              className={`w-full mx-auto ${isMobile ? 'h-full' : ''} space-y-6 sm:space-y-8 md:space-y-10`}
            >
              <motion.div variants={itemVariants} className={isMobile ? 'pt-4 px-4' : ''}>
                <SectionTitle>Report Generator</SectionTitle>
              </motion.div>

              {!isMobile && (
                <StyledSteps
                  current={currentStep}
                  onChange={handleStepChange}
                  responsive={false}
                  className="custom-steps"
                >
                  {steps.map(item => (
                    <Step key={item.title} title={item.title} />
                  ))}
                </StyledSteps>
              )}

              <AnimatePresence mode="wait">
                <motion.div
                  key={currentStep}
                  initial="initial"
                  animate="in"
                  exit="out"
                  variants={pageVariants}
                  transition={pageTransition}
                  className={`w-full ${isMobile ? 'flex-grow overflow-y-auto' : ''}`}
                >
                  <StyledCard className={`p-4 ${isMobile ? 'h-full' : ''}`}>
                    {isMobile && (
                      <Title level={4} className="mb-4">
                        {steps[currentStep].title}
                      </Title>
                    )}
                    {steps[currentStep] && steps[currentStep].content}
                  </StyledCard>
                </motion.div>
              </AnimatePresence>

              <div className={`steps-action ${isMobile ? 'fixed bottom-0 left-0 right-0 p-4 bg-white' : 'mt-6'}`}>
                <Space direction={isMobile ? 'vertical' : 'horizontal'} style={{ width: '100%' }}>
                  {currentStep > 0 && currentStep < steps.length - 1 && (
                    <StyledButton onClick={() => prev()} style={isMobile ? { width: '100%' } : {}}>
                      Previous
                    </StyledButton>
                  )}
                  {currentStep < steps.length - 1 && (
                    <StyledButton 
                      type="primary" 
                      onClick={() => next()}
                      disabled={!isStepValid(currentStep)}
                      style={isMobile ? { width: '100%' } : {}}
                    >
                      Next
                    </StyledButton>
                  )}
                  {currentStep === steps.length - 1 && (
                    <StyledButton
                      type="primary"
                      onClick={handleGenerateReport}
                      icon={<ChartBarIcon className="h-5 w-5 mr-2" />}
                      loading={loading}
                      disabled={!isStepValid(currentStep)}
                      style={isMobile ? { width: '100%' } : {}}
                    >
                      Generate {reportSettings.reportType === 'annual' ? 'Annual' : 'Quarterly'} Report
                    </StyledButton>
                  )}
                </Space>
              </div>
            </StyledCard>
          </Col>
        </Row>
      </ConfigProvider>
    </App>
  );
};

export default Reporting;
